import React from 'react';

export const SLIP_PAGE = {width: 8.5, height: 11};

function shipToKey(item) {
  return item.shipTo ? item.shipTo.lines.join('\n') : '';
}

/**
 * One slip per order. Selected orders that share a non-null parentUuid (one checkout) and an
 * identical ship-to are combined onto a single slip. Group order follows the first order of each
 * group, so the output keeps the queue's newest-first order.
 *
 * @param {Array<object>} items LabelQueueItem[]
 * @returns {Array<{key: string, orders: object[]}>}
 */
export function groupSlips(items) {
  const groups = [];
  const byGroupKey = new Map();
  items.forEach(item => {
    const groupKey = item.parentUuid
      ? `${item.parentUuid}|${shipToKey(item)}|${item.recipientName}`
      : null;
    if (groupKey && byGroupKey.has(groupKey)) {
      byGroupKey.get(groupKey).orders.push(item);
      return;
    }
    const group = {key: groupKey || item.uuid, orders: [item]};
    if (groupKey) byGroupKey.set(groupKey, group);
    groups.push(group);
  });
  return groups;
}

/**
 * The queue's `shipTo.lines` are the street address only (the server builds them with an empty
 * name), so the recipient name is added here, first, as on the address labels. Masked orders have
 * no `shipTo`, so the pseudonym never lands in a ship-to block.
 */
export function shipToLines(item) {
  if (!item.shipTo) return [];
  const name = (item.recipientName || '').trim();
  return name ? [name, ...item.shipTo.lines] : item.shipTo.lines;
}

function shortId(uuid) {
  return String(uuid || '').slice(0, 8);
}

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

/**
 * Full-page packing slips: store name, order ids and dates, ship-to, items with variant lines and
 * quantity, and the buyer's message. Never shows prices.
 */
export default function PackingSlips({items, storeName = ''}) {
  const groups = groupSlips(items);

  return (
    <div className="packing-slips">
      {groups.map(group => {
        const [first] = group.orders;
        return (
          <div
            key={group.key}
            className="packing-slip"
            style={{width: `${SLIP_PAGE.width}in`, height: `${SLIP_PAGE.height}in`}}
          >
            <div className="packing-slip__header">
              {storeName && <h2 className="packing-slip__store">{storeName}</h2>}
              <p className="packing-slip__title">Packing slip</p>
            </div>

            <ul className="packing-slip__orders">
              {group.orders.map(order => (
                <li key={order.uuid}>
                  Order {shortId(order.uuid)}
                  {formatDate(order.createdAt) && `, placed ${formatDate(order.createdAt)}`}
                </li>
              ))}
            </ul>

            <div className="packing-slip__ship-to">
              {first.shipTo ? (
                <>
                  <h3>Ship to</h3>
                  <p>{shipToLines(first).join('\n')}</p>
                </>
              ) : (
                <>
                  <h3>For</h3>
                  <p>{first.recipientName}</p>
                </>
              )}
            </div>

            <table className="packing-slip__items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                {group.orders.map(order => (
                  <tr key={order.uuid}>
                    <td>
                      <strong>{order.productName}</strong>
                      {order.variantLines.map(line => (
                        <div key={line} className="packing-slip__variant">{line}</div>
                      ))}
                    </td>
                    <td>{order.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {group.orders.filter(order => order.buyerMessage).map(order => (
              <div key={order.uuid} className="packing-slip__message">
                <h3>Note from the buyer</h3>
                <p>{order.buyerMessage}</p>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
